import { SuggestionDataItem } from 'react-mentions';
import { AgentThreadInput, AiMentionType, ChatbotUserUploadedFile, MenuItemType } from '@nx-frontend/shared/types';

export enum ChatbotMessageRole {
  USER = 'user',
  AI = 'assistant',
}

export enum SystemMessageTypes {
  LIMIT_REACHED = 'limit_reached',
}

export enum AgentThreadTriggeredBy {
  DEFAULT = 'default',
  AUTOMATION = 'automation',
}
export interface ChatbotThread {
  createdAt: number;
  workspaceId: string;
  userId: number;
  title?: string;
  summary?: string;
  updatedAt: number;
  id?: string;
  triggeredBy?: AgentThreadTriggeredBy;
}

export interface ChatBotToolCallContent {
  name: string;
}

export interface ChatbotCitationDocument {
  content?: string;
  id: string;
  internalId: string;
  noteGlobalId?: string;
  title: string;
  url: string;
  updatedAt: number | null;
  workspaceId?: string;
  type?: 'page' | 'guide' | 'portalPage';
  guideCategory?: string;
  guideCategoryUrl?: string;
  portalId?: string;
  portalUrl?: string;
  pagePreviewUrl?: string | null;
  /**
   * Block ID for the document that contains the citation's source
   */
  blockId?: string;
}

export interface ChatbotCitation {
  id: string;
  documentIds: string[];
  end: number;
  start: number;
  text: string;
}

export interface ChatbotMessageContext {
  type: 'note' | 'notesFolder' | 'allPages' | 'portalNote' | 'portalPage';
  id: string;
  title: string;
}

export interface ChatbotMessageContextChunkContent {
  contextDetails: ChatbotMessageContext;
  textTransformed: string;
}

export interface ChatbotActiveToolCall {
  name: string;
  parameters?: Record<string, unknown>;
}

export interface ChatbotMessage {
  id?: string;
  role: ChatbotMessageRole;
  message: string;
  /**
   * Message text after AI's transformation, for internal use only
   */
  messageTransformed?: string;
  documents?: ChatbotCitationDocument[];
  citations?: ChatbotCitation[];
  system?: boolean;
  messageContext?: ChatbotMessageContext;
  activeToolCall?: ChatbotActiveToolCall;
  /**
   * shows LLM's thought process
   */
  activeReasoning?: {
    content: string;
  };
  /**
   * Files uploaded by user for this message
   */
  files?: ChatbotUserUploadedFile[];
}

export interface ChatbotStoredThread extends ChatbotThread {
  messages: ChatbotMessage[];
  input?: AgentThreadInput;
  isWriting: boolean;
  suggestions?: string[];
  dislikeMessages?: string[];
  editMessageId?: string;
}

export interface ChatbotUsage {
  current: number;
  max: number;
}

export interface ChatbotStore {
  threads: {
    [threadId: string]: ChatbotStoredThread;
  };
  tmpMessages?: ChatbotMessage[];
  usage?: ChatbotUsage;
  preferences: {
    searchEnabled: boolean;
    /**
     * Custom assistant logo storedfile UUID
     */
    logoStoredFileUUID: string | null;
    /**
     * Custom assistant name
     */
    name: string | null;
  };
  size: {
    width: number;
    height: number;
  };
  /**
   * Whether user sent feedback. Feedback surveys are triggered by user behaviour.
   */
  behaviourFeedbackDone: boolean;
}

export enum MessageChunkType {
  THREAD = 'thread',
  SUGGESTIONS = 'suggestions',
  DOCS = 'docs',
  TEXT = 'text',
  CITATIONS = 'citations',
  ERROR = 'error',
  DONE = 'done',
  USAGE = 'usage',
  MSG_INFO = 'responseMessage',
  REQUEST_MESSAGE = 'requestMessage',
  LIMIT_ERROR = 'limitError',
  TEXT_STREAM_END = 'textStreamEnd',
  TOOL_CALL = 'toolCall',
  REASONING = 'reasoning',
  MESSAGE_CONTEXT = 'messageContext',
}

export interface MessageChunk {
  type: MessageChunkType;
  content:
    | (ChatbotThread & { id: string })
    | ChatbotCitationDocument[]
    | ChatbotCitation[]
    | string
    | string[]
    | ChatbotUsage
    | ChatBotToolCallContent
    | ChatbotMessageContextChunkContent;
}

export interface PromptSuggestion {
  id: string;
  text: string;
}

export interface MainPageResponse {
  promptSuggestions: PromptSuggestion[];
  // последние треды, сортированы от новых к старым
  lastThreads: {
    id: string;
    title: string;
    createdAt: number;
    updatedAt: number;
  }[];
  usage?: {
    current: number;
    max: number;
  };
  preferences: {
    searchEnabled: boolean;
    logoStoredFileUUID: string | null;
    name: string | null;
  };
  behaviourFeedbackDone: boolean;
}

export interface ChatbotThreadMessageRaw {
  id: string;
  threadId: string;
  role: ChatbotMessageRole;
  text: string;
  textTransformed?: string;
  createdAt: number;
  documents: ChatbotCitationDocument[];
  citations: ChatbotCitation[];
  messageContext?: ChatbotMessageContext;
  files?: ChatbotUserUploadedFile[];
}

export type PromptType = 'promptSuggestion' | 'userEntered' | 'chatSuggestion';
export interface AiMentionItem {
  target: AiMentionType;
  target_id: string;
  display_text: string;
  url?: string;
}

export interface AiMentionCategory {
  items: AiMentionItem[];
  total: number;
}
export type AiMentionsResponse = {
  [key in AiMentionType]: AiMentionCategory;
};

export interface AiMentionsStore {
  items: SuggestionDataItem[];
  showAll?: AiMentionType;
  isFetching: boolean;
  query?: string;
  total: Partial<Record<keyof AiMentionsResponse, number>> | number;
  isLazyFetching: boolean;
}

export type UserLocationType = MenuItemType | 'attachment';

export interface UserLocation {
  type: UserLocationType;
  id: string | null;
  title: string | null;
}

export interface AgentFileInput {
  filename: string;
  name?: string;
  sfUUID?: string;
}
export type AgentInput = Record<string, string | AgentFileInput>;

export type ChatBotAttachedFileUploadingState = 'uploading' | 'done' | 'error';
export interface ChatBotAttachedFile {
  file: File;
  uploaded: number;
  uploadState: ChatBotAttachedFileUploadingState;
  cancelUpload?: () => void;
  tempStoredFileName?: string;
}
